import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, UserX, UserCheck, Users } from "lucide-react";
import { toast } from "../lib/toast";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/ui/PageHeader";
import { BulkActionBar } from "../components/ui/BulkActionBar";
import { Highlight } from "../components/ui/Highlight";
import { InlineEdit } from "../components/ui/InlineEdit";
import { Pagination } from "../components/ui/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { Toolbar, ToolbarGrow } from "../components/ui/Toolbar";
import { RowAction, RowActions } from "../components/ui/RowAction";
import { StatusPill } from "../components/ui/StatusPill";
import { Table, type TableColumn } from "../components/ui/Table";
import { api, apiErrorMessage } from "../lib/api";
import { formatDate } from "../lib/format";
import { useDebouncedValue } from "../lib/useClientTable";
import { useMinimumDuration } from "../lib/useMinimumDuration";

interface Contact {
  _id: string;
  phone: string;
  name: string | null;
  tags: string[];
  isOptedOut: boolean;
  optedOutAt: string | null;
  consentSource: string;
  createdAt: string;
}

interface ContactsResponse {
  items: Contact[];
  nextCursor: string | null;
  hasMore: boolean;
  /** Total matching contacts, so the bar can show a page count and offer jump-to-last. */
  total?: number;
}

function parseTags(raw: string): string[] {
  return raw
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export function ContactsPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  // Server-side search: /contacts accepts `search` and matches phone + name in SQL. Debounced
  // so a request is not issued per keystroke.
  const debouncedSearch = useDebouncedValue(search, 250);
  const [pageSize, setPageSize] = useState(25);
  /** Cursors for pages 2..n; length + 1 is the current page number. */
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  // Set only while the user has jumped to the final page; any other navigation clears it.
  const [atLast, setAtLast] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [phone, setPhone] = useState("");
  const [contactName, setContactName] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [selected, setSelected] = useState<Set<React.Key>>(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const cursor = cursorStack.at(-1);

  const listQuery = useQuery({
    // Still prefixed with "contacts", so the existing invalidateQueries({ queryKey: ["contacts"] })
    // in every mutation continues to match by prefix and refetch the visible page.
    // atLast is part of the key: without it, toggling the jump would not refetch and the
    // button would appear to do nothing.
    queryKey: ["contacts", debouncedSearch, pageSize, cursor ?? null, atLast],
    queryFn: async () =>
      (
        await api.get<ContactsResponse>("/contacts", {
          params: {
            limit: pageSize,
            ...(cursor && !atLast ? { cursor } : {}),
            ...(atLast ? { last: true } : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
          },
        })
      ).data,
  });

  const listQueryLoading = useMinimumDuration(listQuery.isLoading);

  // A new filter or page size invalidates every cursor already collected — they were issued
  // against a different result set, so paging with them would silently show the wrong rows.
  useEffect(() => {
    setCursorStack([]);
  }, [debouncedSearch, pageSize]);

  function openCreate() {
    setEditing(null);
    setPhone("");
    setContactName("");
    setTagsInput("");
    setEditorOpen(true);
  }

  function openEdit(row: Contact) {
    setEditing(row);
    setPhone(row.phone);
    setContactName(row.name ?? "");
    setTagsInput(row.tags.join(", "));
    setEditorOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const tags = parseTags(tagsInput);
      if (editing) {
        return (
          await api.patch(`/contacts/${editing._id}`, {
            name: contactName.trim() || undefined,
            tags,
          })
        ).data;
      }
      return (
        await api.post("/contacts", {
          phone: phone.trim(),
          name: contactName.trim() || undefined,
          tags,
        })
      ).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Contact updated." : "Contact added.");
      setEditorOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not save the contact.")),
  });

  const optOutMutation = useMutation({
    mutationFn: async ({
      id,
      isOptedOut,
    }: {
      id: string;
      isOptedOut: boolean;
    }) => {
      if (isOptedOut) {
        return api.delete(`/contacts/${id}/opt-out`);
      }
      return api.post(`/contacts/${id}/opt-out`);
    },
    onSuccess: (_, variables) => {
      toast.success(
        variables.isOptedOut ? "Contact opted back in." : "Contact opted out."
      );
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not update opt-out status.")),
  });

  /**
   * Separate from saveMutation because that one is bound to the modal's form state and closes it
   * on success. This patches a single field from the row and leaves the rest of the page alone.
   */
  const saveInlineMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string | null }) =>
      api.patch(`/contacts/${id}`, { name }),
    onSuccess: () => {
      toast.success("Contact updated.");
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not update the contact.")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/contacts/${id}`),
    onSuccess: () => {
      toast.success("Contact deleted.");
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not delete the contact.")),
  });

  const displayContacts = listQuery.data?.items ?? [];

  /**
   * Bulk operations loop the existing single-item endpoints rather than introducing a batch API —
   * this task has no backend scope. Requests run sequentially, not with Promise.all: firing N
   * deletes at once against the same table is how you trip the rate limiter and get a partial
   * result that is harder to reason about than a slower, ordered pass.
   */
  async function runBulk(
    ids: string[],
    each: (id: string) => Promise<unknown>,
    describe: (ok: number, failed: number) => string
  ) {
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await each(id);
        ok += 1;
      } catch {
        // Keep going: one failure must not abandon the rest of the batch.
        failed += 1;
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    setBulkDeleteOpen(false);
    void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    if (failed > 0) toast.error(describe(ok, failed));
    else toast.success(describe(ok, failed));
  }

  const selectedContacts = displayContacts.filter((c) => selected.has(c._id));

  const CONTACT_COLUMNS: TableColumn<Contact>[] = [
    {
      key: "phone",
      header: "Phone",
      render: (row) => (
        <Highlight
          text={row.phone}
          query={search}
          className="font-medium text-text"
        />
      ),
    },
    {
      key: "name",
      header: "Name",
      render: (row) => (
        <InlineEdit
          value={row.name ?? ""}
          ariaLabel={`Name for ${row.phone}`}
          placeholder="Add a name"
          onSave={async (next) => {
            // Awaited but not returned: onSave is typed Promise<void>, and mutateAsync resolves
            // to the axios response. Awaiting still lets InlineEdit settle before re-rendering.
            await saveInlineMutation.mutateAsync({
              id: row._id,
              name: next || null,
            });
          }}
        >
          {row.name ? (
            <Highlight text={row.name} query={search} className="text-text" />
          ) : null}
        </InlineEdit>
      ),
    },
    {
      key: "tags",
      header: "Tags",
      render: (row) =>
        row.tags.length === 0 ? (
          <span className="text-text-muted">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.tags.map((tag) => (
              <Badge key={tag} tone="neutral">
                {tag}
              </Badge>
            ))}
          </div>
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "130px",
      render: (row) => (
        <StatusPill status={row.isOptedOut ? "OPTED_OUT" : "ACTIVE"} />
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      width: "130px",
      hideBelowMd: true,
      render: (row) => (
        <span className="whitespace-nowrap text-text-muted">
          {formatDate(row.createdAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "230px",
      align: "right",
      render: (row) => (
        <RowActions>
          <RowAction onClick={() => openEdit(row)}>Edit</RowAction>
          <RowAction
            disabled={
              optOutMutation.isPending &&
              (optOutMutation.variables as { id: string })?.id === row._id
            }
            icon={
              row.isOptedOut ? (
                <UserCheck className="size-3.5" aria-hidden />
              ) : (
                <UserX className="size-3.5" aria-hidden />
              )
            }
            onClick={() =>
              optOutMutation.mutate({ id: row._id, isOptedOut: row.isOptedOut })
            }
          >
            {row.isOptedOut ? "Opt in" : "Opt out"}
          </RowAction>
          <RowAction
            tone="danger"
            icon={<Trash2 className="size-3.5" aria-hidden />}
            onClick={() => setDeleteTarget(row)}
          >
            Delete
          </RowAction>
        </RowActions>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        sticky
        title="Contacts"
        description="Manage your contact list, tags, and opt-out status."
        actions={
          <Button
            leftIcon={<Plus className="size-4" aria-hidden />}
            onClick={openCreate}
          >
            Add contact
          </Button>
        }
      />

      {listQuery.isError && (
        <p
          role="alert"
          className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger"
        >
          {apiErrorMessage(listQuery.error, "Could not load contacts.")}
        </p>
      )}

      <Toolbar sticky>
        <ToolbarGrow className="sm:max-w-xs">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search phone or name…"
          />
        </ToolbarGrow>
      </Toolbar>

      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          noun="contact"
          onClear={() => setSelected(new Set())}
        >
          <Button
            size="sm"
            variant="secondary"
            disabled={bulkBusy}
            onClick={() =>
              void runBulk(
                selectedContacts.filter((c) => !c.isOptedOut).map((c) => c._id),
                (id) => api.post(`/contacts/${id}/opt-out`),
                (ok, failed) =>
                  failed
                    ? `Opted out ${ok}, ${failed} failed.`
                    : `Opted out ${ok} contact${ok === 1 ? "" : "s"}.`
              )
            }
          >
            Opt out
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={bulkBusy}
            onClick={() => setBulkDeleteOpen(true)}
          >
            Delete
          </Button>
        </BulkActionBar>
      )}

      <Table
        stickyFirstColumn
        stickyHeader
        selectedIds={selected}
        onSelectionChange={setSelected}
        columns={CONTACT_COLUMNS}
        data={displayContacts}
        getRowId={(row) => row._id}
        loading={listQueryLoading}
        emptyState={
          debouncedSearch ? (
            <EmptyState
              icon={<Users className="size-6" aria-hidden />}
              title={`No results for "${debouncedSearch}"`}
              description="Try a different phone number or name, or clear the search."
            />
          ) : (
            <EmptyState
              icon={<Users className="size-6" aria-hidden />}
              title="No contacts yet"
              description="Add contacts to manage consent, tags, and opt-out status."
              action={<Button onClick={openCreate}>Add contact</Button>}
            />
          )
        }
      />
      <Modal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        title={`Delete ${selected.size} contact${
          selected.size === 1 ? "" : "s"
        }?`}
        description="This removes them permanently. Messages already sent to them are not affected."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setBulkDeleteOpen(false)}
            >
              Keep them
            </Button>
            <Button
              variant="danger"
              loading={bulkBusy}
              onClick={() =>
                void runBulk(
                  selectedContacts.map((c) => c._id),
                  (id) => api.delete(`/contacts/${id}`),
                  (ok, failed) =>
                    failed
                      ? `Deleted ${ok}, ${failed} failed.`
                      : `Deleted ${ok} contact${ok === 1 ? "" : "s"}.`
                )
              }
            >
              Delete {selected.size}
            </Button>
          </div>
        }
      />

      <Pagination
        mode="cursor"
        total={listQuery.data?.total}
        page={
          atLast && listQuery.data?.total
            ? Math.max(1, Math.ceil(listQuery.data.total / pageSize))
            : cursorStack.length + 1
        }
        pageSize={pageSize}
        itemCount={displayContacts.length}
        hasMore={listQuery.data?.hasMore ?? false}
        onFirst={() => {
          setAtLast(false);
          setCursorStack([]);
        }}
        onLast={() => setAtLast(true)}
        onPrev={() => {
          setAtLast(false);
          setCursorStack((stack) => stack.slice(0, -1));
        }}
        onNext={() =>
          setCursorStack((stack) =>
            listQuery.data?.nextCursor
              ? [...stack, listQuery.data.nextCursor]
              : stack
          )
        }
        onPageSizeChange={setPageSize}
      />

      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? "Edit contact" : "Add contact"}
        description={
          editing
            ? "Update this contact's details."
            : "Add a new contact to your list."
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={saveMutation.isPending}
              disabled={!editing && phone.trim() === ""}
              onClick={() => saveMutation.mutate()}
            >
              {editing ? "Save changes" : "Add contact"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Phone number"
            placeholder="+1 555 000 0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={editing !== null}
            autoFocus={editing === null}
          />

          <Input
            label="Name (optional)"
            placeholder="Jane Smith"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            autoFocus={editing !== null}
          />

          <Input
            label="Tags (optional)"
            placeholder="vip, newsletter, promo"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            helperText="Comma-separated list of tags."
          />
        </div>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.phone ?? "this contact"}?`}
        description="This contact will be permanently removed. This cannot be undone."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() =>
                deleteTarget && deleteMutation.mutate(deleteTarget._id)
              }
            >
              Delete
            </Button>
          </div>
        }
      />
    </div>
  );
}
